/* global globalThis */
import Keycloak from "keycloak-js";
import {
  saveKeycloakDetails,
  clearKeycloakStorage,
  setGalleryStorage,
} from "./KeycloakStorage";
import { KEYCLOAK } from "../../Services/APIConstants/api.constant";
import { APP_CONST } from "../../Constants/app.constant";
import { localStorageGetItem } from "../../utils/Storage/storageUtils";
import { revokeAccessToken as revokeToken } from "../../Services/auth.service";

let keycloak = null;

/**
 * Initialize keycloak instance with config from globalThis
 */
const getKeycloakInstance = () => {
  if (!keycloak) {
    const keycloakConfig = {
      url: globalThis?.API_BASE_URL + KEYCLOAK,
      realm: globalThis?.SSO_REALM,
      clientId: globalThis?.SSO_CLIENT_ID,
      redirectUri: globalThis?.SSO_REDIRECT_URL,
    };
    keycloak = new Keycloak(keycloakConfig);
  }
  return keycloak;
};

// Track last user activity timestamp
let lastActivityTime = Date.now();
let tokenRefreshInterval = null;
let isMonitoringActive = false;
let isLoggingOut = false; // Flag to prevent re-authentication during logout

/**
 * Update last activity timestamp
 */
const updateActivity = () => {
  lastActivityTime = Date.now();
};

/**
 * Handle visibility change - check token validity when tab becomes visible
 */
const handleVisibilityChange = async () => {
  // Only act when tab becomes visible
  if (!document.hidden) {
    // Check if user has been idle for too long
    if (isUserIdle()) {
      logoutKeycloak();
      return;
    }

    // Check if token exists in storage
    const storedToken = localStorageGetItem(
      APP_CONST.KEYCLOAK_STORAGE_KEYS.ACCESS_TOKEN,
    );
    const tokenExp = localStorageGetItem(APP_CONST.KEYCLOAK_STORAGE_KEYS.EXP);

    if (!tokenExp) {
      // Trigger re-authentication instead of logout
      globalThis?.location.reload();
      return;
    }

    // Validate token format - detect corrupted/tampered tokens
    if (!isValidJWTFormat(storedToken)) {
      clearKeycloakStorage();
      globalThis?.location.reload();
      return;
    }

    // Validate and refresh existing token
    const result = await validateAndRefreshToken("tab visibility");
    if (result.shouldLogout) {
      // On tab visibility, prefer re-authentication over logout
      // Clear storage and reload to trigger fresh authentication

      clearKeycloakStorage();
      globalThis?.location.reload();
    }
  }
};

/**
 * Setup activity listeners to track user interactions
 */
const setupActivityListeners = () => {
  const events = ["mousedown", "mouseup", "keydown", "scroll", "touchstart"];

  for (const event of events) {
    document.addEventListener(event, updateActivity, true);
  }

  // Add visibility change listener to detect when tab becomes active
  document.addEventListener("visibilitychange", handleVisibilityChange);
};

/**
 * Remove activity listeners
 */
const removeActivityListeners = () => {
  const events = ["mousedown", "mouseup", "keydown", "scroll", "touchstart"];

  for (const event of events) {
    document.removeEventListener(event, updateActivity, true);
  }

  // Remove visibility change listener
  document.removeEventListener("visibilitychange", handleVisibilityChange);
};

/**
 * Check if user is idle
 * @returns {boolean} - True if user has been idle for more than threshold
 */
const isUserIdle = () => {
  const idleTime = (Date.now() - lastActivityTime) / 1000; // Convert to seconds
  return idleTime >= APP_CONST.KEYCLOAK_CONFIG.IDLE_TIME_THRESHOLD;
};

/**
 * Validate JWT token format
 * @param {string} token - JWT token
 * @returns {boolean} - True if token has valid JWT format
 */
const isValidJWTFormat = (token) => {
  if (!token || typeof token !== "string") return false;
  // JWT should have 3 parts separated by dots
  const parts = token.split(".");
  return parts.length === 3 && parts.every((part) => part.length > 0);
};

/**
 * Check if token is expired
 * @param {number} exp - Token expiration timestamp
 * @returns {boolean} - True if token is expired
 */
const isTokenExpired = (exp) => {
  if (!exp) return true;
  const currentTime = Math.floor(Date.now() / 1000);
  return currentTime >= exp;
};

/**
 * Check if token needs refresh
 * @param {number} exp - Token expiration timestamp
 * @returns {boolean} - True if token should be refreshed
 */
const shouldRefreshToken = (exp) => {
  if (!exp) return true;
  const currentTime = Math.floor(Date.now() / 1000);
  const timeUntilExpiry = exp - currentTime;
  return timeUntilExpiry <= APP_CONST.KEYCLOAK_CONFIG.TIME_TO_REFRESH;
};

/**
 * Validate and refresh token if needed
 * @param {string} context - Context for logging (e.g., "visibility change", "monitoring")
 * @returns {Promise<{success: boolean, shouldLogout: boolean}>}
 */
const validateAndRefreshToken = async (context = "token check") => {
  // Get token from storage
  const storedToken = localStorageGetItem(
    APP_CONST.KEYCLOAK_STORAGE_KEYS.ACCESS_TOKEN,
  );
  const tokenExp = localStorageGetItem(APP_CONST.KEYCLOAK_STORAGE_KEYS.EXP);

  // No token found
  if (!tokenExp) {
    console.warn(`No token expiration found during ${context}`);
    return { success: false, shouldLogout: true };
  }

  // Validate token format - detect corrupted/tampered tokens
  if (!isValidJWTFormat(storedToken)) {
    console.warn(
      `Invalid token format detected during ${context}, forcing re-authentication`,
    );
    return { success: false, shouldLogout: true };
  }

  // Token doesn't need refresh yet and is still valid
  if (!shouldRefreshToken(tokenExp) && !isTokenExpired(tokenExp)) {
    return { success: true, shouldLogout: false };
  }

  // Token is expired or expiring soon - attempt refresh
  try {
    const kc = getKeycloakInstance();
    // Load tokens from storage into keycloak instance
    const storedRefreshToken = localStorageGetItem(
      APP_CONST.KEYCLOAK_STORAGE_KEYS.REFRESH_TOKEN,
    );
    kc.token = storedToken;
    kc.refreshToken = storedRefreshToken;

    // Initialize keycloak instance properties required by updateToken()
    try {
      kc.authenticated = true;
      kc.tokenParsed = JSON.parse(atob(storedToken.split(".")[1]));
      kc.refreshTokenParsed = JSON.parse(
        atob(storedRefreshToken.split(".")[1]),
      );
    } catch (parseError) {
      console.error("Failed to parse tokens, clearing storage", parseError);
      clearKeycloakStorage();
      return { success: false, shouldLogout: true };
    }

    // Use TIME_TO_REFRESH as minValidity to match our refresh threshold
    const refreshed = await kc.updateToken(
      APP_CONST.KEYCLOAK_CONFIG.TOKEN_UPDATE_BUFFER,
    );

    if (refreshed) {
      saveKeycloakDetails(kc);
      return { success: true, shouldLogout: false };
    }

    console.warn(`Token refresh returned false during ${context}`);
    return { success: false, shouldLogout: true };
  } catch (error) {
    console.error(`Token refresh failed during ${context}`, error);
    return { success: false, shouldLogout: true };
  }
};

/**
 * Check whether the stored auth artifacts are safe to use on app start.
 */
const hasValidStoredToken = (storedToken, tokenExp) =>
  storedToken &&
  tokenExp &&
  !isTokenExpired(tokenExp) &&
  isValidJWTFormat(storedToken);

/**
 * Apply previously retrieved tokens to an existing Keycloak instance.
 */
const applyStoredTokensToKc = (kc, token, refreshToken) => {
  kc.token = token;
  kc.refreshToken = refreshToken;
};

/**
 * Build Keycloak init options depending on whether we have a valid token cach
 */
const buildInitOptions = (tokenIsValid, storedToken, storedRefreshToken) =>
  tokenIsValid && storedToken && storedRefreshToken
    ? {
      onLoad: "check-sso", // Fast SSO check - reuses existing valid token
      token: storedToken,
      refreshToken: storedRefreshToken,
      checkLoginIframe: false, // Disable iframe check for better performance
    }
    : {
      onLoad: "login-required", // Full login flow - fetches fresh tokens
      checkLoginIframe: false,
    };

/**
 * Trigger Keycloak login, preserving the current route.
 */
const redirectToLogin = () => {
  getKeycloakInstance().login({
    redirectUri: globalThis?.location.href,
  });
};

/**
 * Attempts a token refresh using the stored tokens.
 * Returns { success: true, authenticated: true } on success, or null to continue the main flow.
 * Throws if kc.updateToken itself throws (so caller can clear storage and proceed).
 */
const tryRefreshWithStoredTokens = async (
  storedToken,
  storedRefreshToken,
  onAuthSuccess,
) => {
  const kc = getKeycloakInstance();
  applyStoredTokensToKc(kc, storedToken, storedRefreshToken);

  const refreshed = await kc.updateToken(-1); // Force refresh
  if (!refreshed) return null;

  // Save updated token details
  saveKeycloakDetails(kc);

  // Start token refresh monitoring
  startTokenRefreshMonitoring();

  // Call success callback if provided (kept identical to original behavior)
  onAuthSuccess();

  return { success: true, authenticated: true };
};

/**
 * Initialize Keycloak authentication
 * Handles token validation, refresh, and new authentication
 * @param {Function} onAuthSuccess - Callback function to execute on successful authentication
 */
export const initKeycloak = async (onAuthSuccess) => {
  try {
    // Prevent re-authentication if logout is in progress
    if (isLoggingOut) {
      return { success: false, authenticated: false };
    }

    // Step 1: Check if current stored token is valid or expired
    const storedToken = localStorageGetItem(
      APP_CONST.KEYCLOAK_STORAGE_KEYS.ACCESS_TOKEN,
    );
    const storedRefreshToken = localStorageGetItem(
      APP_CONST.KEYCLOAK_STORAGE_KEYS.REFRESH_TOKEN,
    );
    const tokenExp = localStorageGetItem(APP_CONST.KEYCLOAK_STORAGE_KEYS.EXP);

    // Check if token is valid
    const tokenIsValid = hasValidStoredToken(storedToken, tokenExp);

    // Step 2: If valid token exists, bypass initialization
    if (tokenIsValid) {
      const kc = getKeycloakInstance();
      applyStoredTokensToKc(kc, storedToken, storedRefreshToken);

      // Start token refresh monitoring
      startTokenRefreshMonitoring();

      // Call success callback if provided (preserved)
      onAuthSuccess();

      return { success: true, authenticated: true };
    }

    // Step 3 & 4: Handle token expiration scenarios
    if (storedToken && isTokenExpired(tokenExp) && storedRefreshToken) {
      try {
        // Step 5: Token expired but refresh token available - attempt refresh
        const refreshedResult = await tryRefreshWithStoredTokens(
          storedToken,
          storedRefreshToken,
          onAuthSuccess,
        );
        if (refreshedResult) return refreshedResult;
      } catch (refreshError) {
        console.warn(
          "Token refresh failed, initiating new login",
          refreshError,
        );
        // Step 4: Refresh token also invalid, clear storage and proceed to new init
        clearKeycloakStorage();
      }
    }

    // Initialize Keycloak with appropriate strategy
    // Only use stored tokens if access token is valid
    const initOptions = buildInitOptions(
      tokenIsValid,
      storedToken,
      storedRefreshToken,
    );

    // Step 3: No token available or Step 4: Both tokens invalid - Initialize Keycloak

    const kc = getKeycloakInstance();
    const authenticated = await kc.init(initOptions);

    if (authenticated) {
      // Step 6: Save keycloak details on successful authentication
      saveKeycloakDetails(kc);

      setGalleryStorage();
      // Start token refresh monitoring
      startTokenRefreshMonitoring();

      // Call success callback if provided (preserved)
      onAuthSuccess();

      return { success: true, authenticated: true };
    }

    // Step 7: Authentication error - trigger login
    console.error("Authentication failed, redirecting to login");
    redirectToLogin();
    return { success: false, authenticated: false };
  } catch (error) {
    // Step 7: Handle authentication errors
    console.error("Keycloak initialization error:", error);
    try {
      redirectToLogin();
    } catch (loginError) {
      console.error("Failed to redirect to login:", loginError);
    }
    return { success: false, authenticated: false, error };
  }
};
/**
 * Revoke the current access token at Keycloak (RFC 7009).
 * Must be called BEFORE clearing storage so the token is still available.
 * The frontend client is the issuing client, so this will succeed.
 */
const revokeAccessToken = async () => {
  const token = localStorageGetItem(APP_CONST.KEYCLOAK_STORAGE_KEYS.ACCESS_TOKEN);
  if (!token) return;

  try {
    const payload = new URLSearchParams({
      token,
      token_type_hint: "access_token",
      client_id: globalThis?.SSO_CLIENT_ID,
    }).toString();

    await revokeToken(payload);
  } catch (error) {
    console.error("Token revocation failed:", error);
    // Non-blocking — logout proceeds regardless
  }
};
/**
 * Logout from Keycloak
 * Clears local storage and redirects to logout URL
 */
export const logoutKeycloak = async () => {
  // Set flag to prevent re-authentication during logout
  isLoggingOut = true;
  // Revoke the access token BEFORE clearing storage (token must still be readable)
  await revokeAccessToken();
  // Clear all keycloak storage
  clearKeycloakStorage();

  // Stop token refresh monitoring
  stopTokenRefreshMonitoring();

  // Logout from Keycloak with redirect
  try {
    const kc = getKeycloakInstance();
    kc.logout({
      redirectUri: globalThis?.SSO_REDIRECT_URL,
    });
  } catch (error) {
    console.error("Keycloak logout failed, forcing redirect", error);
    // Reset flag on error and force redirect anyway
    isLoggingOut = false;
    // Force redirect if keycloak logout fails
    globalThis.location.href =
      globalThis?.SSO_REDIRECT_URL || globalThis.location.origin;
  }
};

/**
 * Monitor token expiration and refresh automatically
 * Checks if user is idle and logs out after 40 minutes of inactivity
 * Otherwise refreshes token when about to expire
 */
const monitorTokenRefresh = async () => {
  // Check if user is idle for 40 minutes - ONLY logout on idle timeout
  if (isUserIdle()) {
    logoutKeycloak();
    return;
  }

  // User is active - validate and refresh token if needed
  const result = await validateAndRefreshToken("periodic monitoring");

  if (!result.success && result.shouldLogout) {
    // Token refresh failed but user is active - don't logout, just continue monitoring
    // The next iteration will try again, or user will be prompted on next page interaction
    console.warn(
      "Token refresh failed but user is active, will retry on next check",
    );
  }

  // Schedule next check regardless of refresh success/failure
  tokenRefreshInterval = setTimeout(
    monitorTokenRefresh,
    APP_CONST.KEYCLOAK_CONFIG.REFRESH_INTERVAL,
  );
};

/**
 * Start token refresh monitoring
 * Sets up activity listeners and begins periodic token checks
 */
const startTokenRefreshMonitoring = () => {
  // Prevent duplicate monitoring
  if (isMonitoringActive) {
    return;
  }

  // Reset activity time when monitoring starts - start counting from authentication success
  lastActivityTime = Date.now();

  // Setup activity tracking
  setupActivityListeners();

  // Mark monitoring as active
  isMonitoringActive = true;

  // Start monitoring

  monitorTokenRefresh();
};

/**
 * Stop token refresh monitoring
 * Clears the monitoring interval and removes event listeners
 */
const stopTokenRefreshMonitoring = () => {
  if (tokenRefreshInterval) {
    clearTimeout(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }

  if (isMonitoringActive) {
    removeActivityListeners();
    isMonitoringActive = false;
  }
};

export { getKeycloakInstance as keycloak };
